import {
  Button,
  cn,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import { AppImage } from "@/shared/ui/AppImage";
import type {
  CreateEventCategoryDTO,
  UpdateEventCategoryDTO,
} from "../../domain/contracts";
import {
  EVENT_CATEGORY_LOGO_OPTIONS,
  isEventCategoryLogoKey,
} from "../lib/event-category-logos";

const EventCategoryFormSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(1, "ຕ້ອງປ້ອນ slug")
    .max(64)
    .regex(/^[a-z0-9_]+$/, "slug ຕ້ອງເປັນ a-z, 0-9, _"),
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່"),
  nameEn: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  logoStorageKey: z
    .string()
    .nullable()
    .optional()
    .refine(
      (value) =>
        value == null ||
        value === "" ||
        isEventCategoryLogoKey(value) ||
        value.startsWith("uploads/"),
      { message: "ເລືອກ logo ຈາກລາຍການ" },
    ),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean(),
});

type EventCategoryFormValues = z.infer<typeof EventCategoryFormSchema>;

type EventCategoryFormProps = {
  initialValues?: Partial<EventCategoryFormValues>;
  slugDisabled?: boolean;
  onSubmit: (values: CreateEventCategoryDTO | UpdateEventCategoryDTO) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

function EventCategoryLogoPicker({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (key: string | null) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="font-medium text-sm">Logo</p>
        <p className="text-muted-foreground text-xs">
          ເລືອຈາກ logo ມາດຕະຖານ 2 ແບບ — ໃຊ້ໃນຟອມງານ ແລະ ລາຍການ
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {EVENT_CATEGORY_LOGO_OPTIONS.map((option) => {
          const selected = value === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(selected ? null : option.key)}
              className={cn(
                "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                selected
                  ? "border-primary bg-primary/5 ring-2 ring-primary"
                  : "hover:bg-muted/50",
              )}
            >
              <div className="size-14 shrink-0 overflow-hidden rounded-md border bg-muted p-1.5">
                <img
                  src={option.src}
                  alt=""
                  className="size-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm">{option.labelLo}</p>
                <p className="text-muted-foreground text-xs">{option.labelEn}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="size-12 shrink-0 overflow-hidden rounded-lg border bg-muted">
          <AppImage
            src={value ?? undefined}
            alt=""
            className="size-full"
            fit="contain"
          />
        </div>
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(null)}
          >
            ລຶບ logo
          </Button>
        ) : (
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ເລືອກ logo</p>
        )}
      </div>
    </div>
  );
}

export function EventCategoryForm({
  initialValues,
  slugDisabled = false,
  onSubmit,
  onCancel,
  submitting,
}: EventCategoryFormProps) {
  const methods = RHF.useForm<EventCategoryFormValues>({
    resolver: zodResolver(EventCategoryFormSchema),
    defaultValues: {
      slug: initialValues?.slug ?? "",
      name: initialValues?.name ?? "",
      nameEn: initialValues?.nameEn ?? null,
      description: initialValues?.description ?? null,
      logoStorageKey: initialValues?.logoStorageKey ?? null,
      sortOrder: initialValues?.sortOrder ?? 0,
      isActive: initialValues?.isActive ?? true,
    },
  });

  const logoKey = methods.watch("logoStorageKey");

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          slug: vals.slug.trim(),
          name: vals.name.trim(),
          nameEn: vals.nameEn?.trim() || null,
          description: vals.description?.trim() || null,
          logoStorageKey: vals.logoStorageKey ?? null,
          sortOrder: vals.sortOrder,
          isActive: vals.isActive,
        });
      }}
      className="space-y-4 py-6"
    >
      <FormInput
        name="slug"
        label="Slug"
        placeholder="stage_show"
        required
        disabled={slugDisabled}
      />
      <FormInput name="name" label="ຊື່ (ລາວ)" required />
      <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="Stage Show" />
      <FormTextarea
        name="description"
        label="ຄຳອະທິບາຍ"
        rows={3}
        placeholder="ຄຳອະທິບາຍສັ້ນໆ..."
      />

      <EventCategoryLogoPicker
        value={logoKey}
        onChange={(key) =>
          methods.setValue("logoStorageKey", key, { shouldDirty: true })
        }
      />

      <FormNumber name="sortOrder" label="ລຳດັບການຈັດ" min={0} />
      <FormCheckboxCard name="isActive" label="ໃຊ້ງານ" />

      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
